import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { HStack } from "@repo/components/src/ui/hstack";
import { AlertCircleIcon, X } from "lucide-react-native";
import { ComponentProps, useCallback } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputSelectProps } from "../types";

type Props<TFieldValues extends FieldValues> = (
  | ({
      multiple: true;
    } & Omit<
      FormInputSelectProps<TFieldValues, string[] | null>,
      "placeholder"
    >)
  | ({
      multiple: false;
    } & Omit<FormInputSelectProps<TFieldValues, string | null>, "placeholder">)
) & {
  rounded?: ComponentProps<typeof Button>["rounded"];
  action?: ComponentProps<typeof Button>["action"];
  buttonSize?: ComponentProps<typeof Button>["size"];
  stackSpace?: ComponentProps<typeof HStack>["space"];
};

export default function FormInputButtons<TFieldValues extends FieldValues>(
  props: Props<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    required,
    disabled: disabledProp = false,
    className,
    size,
    options,
    labelSize,
    multiple,
    rounded = "2xl",
    action,
    buttonSize,
    stackSpace = "xs",
  } = props;
  const buttonOnChange = useCallback(
    (
      originalOnChange: (...event: any[]) => void,
      buttonValue: string,
      actualValue?: string[],
    ) =>
      () => {
        if (multiple) {
          if (actualValue?.includes(buttonValue)) {
            originalOnChange(actualValue.filter((a) => a !== buttonValue));
          } else {
            originalOnChange([...(actualValue || []), buttonValue]);
          }
        } else {
          originalOnChange(buttonValue);
        }
      },
    [multiple],
  );
  const getIsSelected = useCallback(
    (value: string | string[], option: string) => {
      if (multiple) return value.includes(option);
      return value === option;
    },
    [multiple],
  );
  return (
    <Controller
      name={name}
      control={control}
      disabled={disabledProp}
      render={({
        field: { onChange, value, disabled, ref },
        fieldState: { error },
      }) => (
        <FormControl
          isInvalid={!!error}
          isRequired={required}
          isDisabled={disabled}
          className={className}
          size={size}
        >
          {!!label && (
            <FormControlLabel>
              <FormControlLabelText size={labelSize || size}>
                {label}
              </FormControlLabelText>
            </FormControlLabel>
          )}
          <HStack
            space={stackSpace}
            className="flex-wrap mt-2"
            ref={focusScrollRef(ref)}
          >
            {options.map((option) => {
              const isSelected = getIsSelected(value, option.value as string);
              return (
                <Button
                  key={option.value}
                  size={buttonSize || size}
                  rounded={rounded}
                  action={action}
                  isDisabled={disabled}
                  aria-selected={isSelected}
                  variant={isSelected ? "solid" : "outline"}
                  className="border items-center group/button-form transition-all"
                  onPress={buttonOnChange(
                    onChange,
                    option.value as string,
                    value,
                  )}
                >
                  <ButtonText>{option.label}</ButtonText>
                  {multiple && (
                    <ButtonIcon
                      as={X}
                      className={`ml-1 opacity-0 group-aria-selected/button-form:opacity-100 transition-all`}
                    />
                  )}
                </Button>
              );
            })}
          </HStack>
          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
            <FormControlErrorText>{error?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
